import type { AvisoDeFacturacion, AvisoDeMensajeria, AvisoDeStripe } from '../../api/ops/backoffice';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Lógica sin estilos de la **Cola de errores** (1.21, G187): una sola lista con los avisos de facturación de Reservaria
 * (`listBillingEvents`), de Stripe de los negocios (`listStripeConnectEvents`) y de los proveedores de mensajería
 * (`listMessagingProviderEvents`). Las colas solo traen el tipo técnico del aviso: **el panel pone el título y la gravedad
 * según el tipo** (G187, M1-19 a). Un tipo que no está en las tablas sale con un título genérico de su cola y sin
 * «Urgente».
 */
export type Origen = 'facturacion' | 'stripe' | 'mensajeria';

/**
 * Las colas que ve cada rol: Soporte, solo los avisos de Stripe de los negocios, en lectura y sin actuar
 * (`plataforma.avisos_pagos.ver`, G1138; roles-permisos, «Plataforma»).
 */
export function origenesDe(superadmin: boolean): readonly Origen[] {
    return superadmin ? ['facturacion', 'stripe', 'mensajeria'] : ['stripe'];
}

/** Nombre de cada cola en la línea gris del aviso (prototipo `ORIG`). */
export const NOMBRE_ORIGEN: Readonly<Record<Origen, string>> = {
    facturacion: 'Facturación de Reservaria',
    stripe: 'Stripe de los negocios',
    mensajeria: 'Mensajería',
};

/** Filtros «Origen de los errores» (prototipo `EF`): Todo y una por cola que el rol ve. */
export const FILTROS: readonly { clave: Origen | null; etiqueta: string }[] = [
    { clave: null, etiqueta: 'Todo' },
    { clave: 'facturacion', etiqueta: 'Facturación' },
    { clave: 'stripe', etiqueta: 'Stripe de negocios' },
    { clave: 'mensajeria', etiqueta: 'Mensajería' },
];

export function filtrosDe(superadmin: boolean): typeof FILTROS {
    const ve = origenesDe(superadmin);
    return FILTROS.filter((f) => f.clave === null || ve.includes(f.clave));
}

/** Un aviso de cualquiera de las tres colas, listo para la fila. */
export interface AvisoDeLaCola {
    /** `origen:id`: único en la lista junta. */
    clave: string;
    id: string;
    origen: Origen;
    titulo: string;
    detalle: string;
    icono: NombreIcono;
    urgente: boolean;
    /** El tipo técnico (`invoice.paid`, `account.updated`, `status`…), al final de la línea gris. */
    tipo: string;
    negocioId: string | null;
    /** Nombre del negocio que trae el aviso (`business_name`, G1140; también el de la mensajería, G1174). */
    negocioNombre: string | null;
    /** El proveedor de mensajería («YCloud»), si lo hay. */
    proveedor: string | null;
    recibido: string;
}

interface Texto {
    titulo: string;
    icono: NombreIcono;
    urgente: boolean;
}

// Títulos y gravedad por tipo (G187). «Urgente»: lo que deja a un negocio sin cobrar, sin su plan o sin mensajes.
const FACTURACION: Readonly<Record<string, Texto>> = {
    'customer.subscription.created': { titulo: 'Alta de suscripción sin aplicar', icono: 'triangle-alert', urgente: true },
    'customer.subscription.updated': { titulo: 'Cambio de suscripción sin aplicar', icono: 'triangle-alert', urgente: true },
    'customer.subscription.deleted': { titulo: 'Baja de suscripción sin aplicar', icono: 'triangle-alert', urgente: true },
    'invoice.paid': { titulo: 'Factura pagada sin aplicar', icono: 'receipt', urgente: false },
    'invoice.payment_failed': { titulo: 'Cobro fallido sin aplicar', icono: 'receipt', urgente: true },
    'checkout.session.completed': { titulo: 'Contratación sin aplicar', icono: 'receipt', urgente: true },
};
const STRIPE: Readonly<Record<string, Texto>> = {
    'account.updated': { titulo: 'Cambio en una cuenta de cobro sin aplicar', icono: 'credit-card', urgente: true },
    'checkout.session.completed': { titulo: 'Pago sin aplicar', icono: 'credit-card', urgente: true },
    'payment_intent.succeeded': { titulo: 'Pago sin aplicar', icono: 'credit-card', urgente: true },
    'payment_intent.payment_failed': { titulo: 'Pago fallido sin aplicar', icono: 'credit-card', urgente: false },
    'charge.refunded': { titulo: 'Reembolso sin aplicar', icono: 'rotate-ccw', urgente: false },
    'charge.dispute.created': { titulo: 'Disputa de un cobro sin aplicar', icono: 'triangle-alert', urgente: true },
};
const MENSAJERIA: Readonly<Record<AvisoDeMensajeria['kind'], Texto>> = {
    message: { titulo: 'Mensaje recibido sin aplicar', icono: 'message-circle', urgente: true },
    status: { titulo: 'Estado de entrega sin aplicar', icono: 'message-circle', urgente: false },
    template_status: { titulo: 'Estado de una plantilla sin aplicar', icono: 'message-circle', urgente: false },
    account_update: { titulo: 'Cambio en la cuenta del proveedor sin aplicar', icono: 'unplug', urgente: true },
    call: { titulo: 'Llamada sin aplicar', icono: 'phone', urgente: false },
    email_event: { titulo: 'Rebote o respuesta de email sin aplicar', icono: 'mail', urgente: false },
};
const RECHAZO: Readonly<Record<NonNullable<AvisoDeMensajeria['reject_reason']>, Texto & { detalle: string }>> = {
    bad_signature: { titulo: 'Aviso con la firma no válida', icono: 'triangle-alert', urgente: false, detalle: 'La firma del aviso no cuadra. No se ha aplicado nada.' },
    unknown_channel: { titulo: 'Aviso de un canal que no tenemos', icono: 'message-circle', urgente: false, detalle: 'El proveedor manda un aviso de un canal que no está registrado. No se ha aplicado nada.' },
    inactive_channel: { titulo: 'Aviso de un canal desactivado', icono: 'unplug', urgente: false, detalle: 'El canal está desactivado. No se ha aplicado nada.' },
    unknown_connection: {
        titulo: 'Número de WhatsApp de un alta desconocida',
        icono: 'unplug',
        urgente: true,
        detalle: 'YCloud avisa de un alta cancelada o que no es nuestra. Ocupa plaza del límite de cuentas hasta que se cierre.',
    },
};

const PROVEEDOR: Readonly<Record<AvisoDeMensajeria['provider'], string>> = {
    meta: 'Meta',
    ycloud: 'YCloud',
    telegram: 'Telegram',
    mail_server: 'Servidor de correo',
    voice_provider: 'Proveedor de voz',
    reservaria: 'Reservaria',
};

/** Qué pasó, según el estado: la parte legible; detrás, el último error técnico si lo hay. */
function detalleDe(status: string, intentos: number, error: string | null | undefined): string {
    const base =
        status === 'needs_review'
            ? 'Necesita revisión: no encaja con el catálogo o con ningún negocio. No se ha aplicado nada.'
            : status === 'outdated'
              ? 'Llegó después de otro más nuevo. Lo hemos parado para no aplicar un estado viejo.'
              : status === 'rejected'
                ? 'Rechazado al llegar. No se ha aplicado nada.'
                : intentos > 1
                  ? `No se ha podido aplicar tras ${String(intentos)} intentos.`
                  : 'No se ha podido aplicar.';
    const tecnico = (error ?? '').trim();
    return tecnico === '' ? base : `${base} ${tecnico}`;
}

export function avisoDeFacturacion(e: AvisoDeFacturacion): AvisoDeLaCola {
    const t = e.status === 'outdated' ? { titulo: 'Aviso fuera de orden', icono: 'clock' as const, urgente: false } : (FACTURACION[e.type] ?? { titulo: 'Aviso de facturación sin aplicar', icono: 'receipt', urgente: false });
    return { clave: `facturacion:${e.id}`, id: e.id, origen: 'facturacion', ...t, detalle: detalleDe(e.status, e.attempts, e.last_error), tipo: e.type, negocioId: e.business_id ?? null, negocioNombre: e.business_name ?? null, proveedor: null, recibido: e.received_at };
}

export function avisoDeStripe(e: AvisoDeStripe): AvisoDeLaCola {
    const t = e.status === 'outdated' ? { titulo: 'Aviso fuera de orden', icono: 'clock' as const, urgente: false } : (STRIPE[e.type] ?? { titulo: 'Aviso de Stripe sin aplicar', icono: 'credit-card', urgente: false });
    return { clave: `stripe:${e.id}`, id: e.id, origen: 'stripe', ...t, detalle: detalleDe(e.status, e.attempts, e.last_error), tipo: e.type, negocioId: e.business_id ?? null, negocioNombre: e.business_name ?? null, proveedor: null, recibido: e.received_at };
}

export function avisoDeMensajeria(e: AvisoDeMensajeria): AvisoDeLaCola {
    const rechazo = e.reject_reason === null || e.reject_reason === undefined ? null : RECHAZO[e.reject_reason];
    const t = rechazo ?? MENSAJERIA[e.kind];
    return {
        clave: `mensajeria:${e.id}`,
        id: e.id,
        origen: 'mensajeria',
        titulo: t.titulo,
        icono: t.icono,
        urgente: t.urgente,
        detalle: rechazo?.detalle ?? detalleDe(e.status, e.attempts, e.last_error),
        tipo: e.kind,
        negocioId: e.business_id ?? null,
        negocioNombre: e.business_name ?? null,
        proveedor: PROVEEDOR[e.provider],
        recibido: e.received_at,
    };
}

/** Las tres colas juntas, del aviso más reciente al más antiguo. */
export function juntar(...listas: readonly (readonly AvisoDeLaCola[])[]): AvisoDeLaCola[] {
    return listas.flat().sort((a, b) => Date.parse(b.recibido) - Date.parse(a.recibido));
}

const dosCifras = (n: number) => String(n).padStart(2, '0');

/** «hoy, 09:12», «ayer, 20:05» o «30/09, 18:02» (prototipo `meta`). */
export function cuandoLlego(iso: string, ahora: Date): string {
    const d = new Date(iso);
    const hora = `${dosCifras(d.getHours())}:${dosCifras(d.getMinutes())}`;
    const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dias = Math.round((dia(ahora) - dia(d)) / 86_400_000);
    if (dias === 0) {
        return `hoy, ${hora}`;
    }
    if (dias === 1) {
        return `ayer, ${hora}`;
    }
    return `${dosCifras(d.getDate())}/${dosCifras(d.getMonth() + 1)}, ${hora}`;
}

/** Línea gris: cola · negocio · cuándo · proveedor · tipo (sin el negocio si no se sabe). */
export function lineaDeAviso(a: AvisoDeLaCola, negocio: string | undefined, ahora: Date): string {
    return [NOMBRE_ORIGEN[a.origen], negocio, cuandoLlego(a.recibido, ahora), a.proveedor, a.tipo].filter((x) => x !== undefined && x !== null && x !== '').join(' · ');
}

/** Texto de la ventana «Reintentar» de la mensajería, que pide motivo (G1137). */
// Provisional hasta C.64.
export const TEXTO_REINTENTAR_MENSAJERIA = 'Vuelve a procesar el aviso tal como llegó del proveedor. Escribe por qué: queda en Auditoría con tu nombre y la hora.';

/** Texto de «Marcar como resuelto» según la cola (prototipo `vcl`). */
export function textoResuelto(origen: Origen): string {
    const como = { facturacion: ', como aviso de facturación de Reservaria', stripe: ', como aviso de Stripe del negocio', mensajeria: ', como aviso del proveedor de mensajería' }[origen];
    return `Sale de la cola sin reprocesarlo${como}. Queda cerrado con tu nombre, la hora y el motivo en Auditoría.`;
}

/**
 * Cursor de la página de la cola: el de cada cola por separado. `null`, su primera página; `false`, ya no le quedan
 * (no se vuelve a pedir). Va como un solo texto para la paginación de siempre (`usePaginacionCursor`).
 */
export type CursoresCola = Readonly<Record<Origen, string | null | false>>;

export function leerCursores(cursor: string | null): CursoresCola {
    if (cursor === null) {
        return { facturacion: null, stripe: null, mensajeria: null };
    }
    try {
        const c = JSON.parse(cursor) as Partial<Record<Origen, unknown>>;
        const uno = (v: unknown): string | null | false => (typeof v === 'string' || v === false ? v : null);
        return { facturacion: uno(c.facturacion), stripe: uno(c.stripe), mensajeria: uno(c.mensajeria) };
    } catch {
        return { facturacion: null, stripe: null, mensajeria: null };
    }
}

/** El cursor de la página siguiente, o null si a ninguna cola le quedan avisos. */
export function siguientesCursores(siguientes: Readonly<Record<Origen, string | null | undefined>>): string | null {
    const v = (x: string | null | undefined): string | false => (typeof x === 'string' && x !== '' ? x : false);
    const c: Record<Origen, string | false> = { facturacion: v(siguientes.facturacion), stripe: v(siguientes.stripe), mensajeria: v(siguientes.mensajeria) };
    return Object.values(c).some((x) => x !== false) ? JSON.stringify(c) : null;
}
